import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage, Sender } from '../types';
import { CosmosLogoIcon } from './icons/CosmosLogoIcon';
import { DocumentIcon } from './icons/DocumentIcon';
import { ChatSearchResults } from './ChatSearchResults';

interface ChatMessageProps {
  message: ChatMessage;
  userName?: string;
  personalSummary?: string;
  rdSummary?: string;
  sessionId?: string;
  profileIncomplete?: boolean;
  aiMessageDbId?: string; // Database message ID for feedback
  onRunExtensiveSearch?: () => void; // Handler to run extensive search
  onSendMessage?: (text: string) => void; // Handler to send a chat message programmatically
  isFreshMessage?: boolean; // Flag to indicate if this is a fresh message (not reloaded)
}

const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Helper function to extract search results from attachments
const extractSearchResults = (attachments: any[]): any[] => {
    const searchResults: any[] = [];
    
    attachments.forEach((attachment: any) => {
        // Check if attachment contains search_results
        if (attachment.search_results && Array.isArray(attachment.search_results)) {
            searchResults.push(...attachment.search_results);
        }
        // Also check if the attachment itself is a search result object
        else if (attachment.score && attachment.metadata && attachment.source_type) {
            searchResults.push(attachment);
        }
    });
    
    return searchResults;
};

export const ChatMessageComponent: React.FC<ChatMessageProps> = ({ 
  message,
  userName = '',
  personalSummary = '',
  rdSummary = '',
  sessionId = '',
  profileIncomplete = false,
  aiMessageDbId = '',
  onRunExtensiveSearch,
  onSendMessage,
  isFreshMessage = false
}) => {
  const isUser = message.sender === Sender.User;
  
  // Use database message ID for feedback if available
  const finalMessageId = aiMessageDbId || message.dbMessageId || message.id;

  // Extract search results from attachments
  const searchResults = message.attachments ? extractSearchResults(message.attachments) : [];
  
  // Extract broad query metadata if present
  const broadQueryMeta = message.attachments?.find((att: any) => att.broad_query_meta)?.broad_query_meta;
  
  // Filter out search result attachments to get regular file attachments
  const regularAttachments = message.attachments ? message.attachments.filter((att: any) => 
    !att.search_results && !(att.score && att.metadata && att.source_type)
  ) : [];

  return (
    <div className={`flex items-start gap-4 p-4 rounded-xl ${isUser ? 'bg-secondary-300/20' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <CosmosLogoIcon />
        </div>
      )}
      <div className="flex-1">
        <div className="markdown-content text-grayscale-1200">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.text}
          </ReactMarkdown>
        </div>
        
        {/* Render search results as cards */}
        {searchResults.length > 0 && (
          <ChatSearchResults 
            searchResults={searchResults}
            userName={userName}
            personalSummary={personalSummary}
            rdSummary={rdSummary}
            sessionId={sessionId}
            aiMessageId={finalMessageId}
            profileIncomplete={profileIncomplete}
            extensiveSearch={message.extensiveSearch}
            onRunExtensiveSearch={onRunExtensiveSearch}
            onSendMessage={onSendMessage}
            isFreshMessage={isFreshMessage}
            broadQueryMeta={broadQueryMeta}
          />
        )}
        
        {/* Render regular file attachments */}
        {regularAttachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {regularAttachments.map((att, index) => (
              <div key={index} className="w-[120px] h-[94px] flex flex-col items-start rounded-lg border border-primary-200 shadow-custom-light relative bg-white">
                <div className="flex-1 self-stretch w-full rounded-t-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                  {att.type && att.type.startsWith('image/') ? (
                    <img src={att.url} alt={att.name} className="w-full h-full object-cover"/>
                  ) : (
                    <DocumentIcon />
                  )}
                </div>
                <div className="p-1 self-stretch rounded-b-lg bg-primary-200">
                  <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-medium leading-none truncate">{att.name}</p>
                  <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-light leading-none">{formatBytes(att.size || 0)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};